import { IFeedBoardFilters, IFeedLeague, IFeedTeam } from '@/types';
import { EventFeedType, EventPeriod, League, Line, StoreName } from '@/constants';
import { useSearchedTeamIds } from '@/stores/composables/feed/useSearchedTeamIds';
import { useSearchedLeagueIds } from '@/stores/composables/feed/useSearchedLeagueIds';
import { useBetsChangedHandler } from '@/stores/composables/feed/useBetsChangedHandler';
import { useFeedResponseProcessor } from '@/stores/composables/feed/useFeedResponseProcessor';
import { FeedPregameItem } from '@/models/feedPregame';
import { usePersistentFilters } from '@/stores/composables/usePersistentFilters';
import { FeedLiveItem } from '@/models/feedLive';
import { useEventStore } from '@/stores/event';
import { ShallowRef } from 'vue';

export const useFeedBoardStore = defineStore(StoreName.FeedBoard, () => {
    const liveItems = shallowRef<FeedLiveItem[]>([]);
    const todayItems = shallowRef<FeedPregameItem[]>([]);
    const earlyItems = shallowRef<FeedPregameItem[]>([]);
    const liveVersion = ref<number>(0);
    const todayVersion = ref<number>(0);
    const earlyVersion = ref<number>(0);
    const liveEventsPositionIdx = ref<Record<string, number>>({});
    const todayEventsPositionIdx = ref<Record<string, number>>({});
    const earlyEventsPositionIdx = ref<Record<string, number>>({});
    const total = ref<number>(0);
    const loading = ref<boolean>(false);
    const isExtraFiltersDisplayed = ref<boolean>(false);
    const customColumns = ref<string[] | null>(null);
    const filters = reactive<IFeedBoardFilters>({
        search: '',
        line: Line.Main,
        periodTypeId: EventPeriod.FullTime,
        customEventId: null,
        isPositionOnly: false,
        isFantasyExclude: false,
    });

    usePersistentFilters(filters, ['isPositionOnly', 'isFantasyExclude']);

    const liveLeagueItems = ref<Record<number, IFeedLeague>>({});
    const todayLeagueItems = ref<Record<number, IFeedLeague>>({});
    const earlyLeagueItems = ref<Record<number, IFeedLeague>>({});
    const liveTeamItems = ref<Record<number, IFeedTeam>>({});
    const todayTeamItems = ref<Record<number, IFeedTeam>>({});
    const earlyTeamItems = ref<Record<number, IFeedTeam>>({});

    const eventStore = useEventStore();

    function reset() {
        liveItems.value = [];
        todayItems.value = [];
        earlyItems.value = [];
        liveVersion.value = 0;
        todayVersion.value = 0;
        earlyVersion.value = 0;
        liveEventsPositionIdx.value = {};
        todayEventsPositionIdx.value = {};
        earlyEventsPositionIdx.value = {};
        liveLeagueItems.value = {};
        todayLeagueItems.value = {};
        earlyLeagueItems.value = {};
        liveTeamItems.value = {};
        todayTeamItems.value = {};
        earlyTeamItems.value = {};
    }

    const { checkResponse: checkLiveResponse, processResponse: processLiveResponse } = useFeedResponseProcessor<FeedLiveItem>(
        EventFeedType.Live,
        liveItems,
        filters,
        'FeedLiveItem',
        liveEventsPositionIdx,
        liveVersion,
        liveLeagueItems,
        liveTeamItems
    );

    const { checkResponse: checkTodayResponse, processResponse: processTodayResponse } = useFeedResponseProcessor<FeedPregameItem>(
        EventFeedType.Today,
        todayItems,
        filters,
        'FeedPregameItem',
        todayEventsPositionIdx,
        todayVersion,
        todayLeagueItems,
        todayTeamItems
    );

    const { checkResponse: checkEarlyResponse, processResponse: processEarlyResponse } = useFeedResponseProcessor<FeedPregameItem>(
        EventFeedType.Early,
        earlyItems,
        filters,
        'FeedPregameItem',
        earlyEventsPositionIdx,
        earlyVersion,
        earlyLeagueItems,
        earlyTeamItems
    );

    const teamItems = computed(() => ({
        ...liveTeamItems.value,
        ...todayTeamItems.value,
        ...earlyTeamItems.value,
    }));

    const leagueItems = computed(() => ({
        ...liveLeagueItems.value,
        ...todayLeagueItems.value,
        ...earlyLeagueItems.value,
    }));

    const searchRef = toRef(filters, 'search');
    const { searchedTeamIds } = useSearchedTeamIds(searchRef, teamItems);
    const { searchedLeagueIds } = useSearchedLeagueIds(searchRef, leagueItems);

    const displayedItems = computed<(FeedLiveItem | FeedPregameItem)[]>(() => {
        const activeItems = [...liveItems.value, ...todayItems.value, ...earlyItems.value];
        const favoriteEvent = unref(eventStore.favoriteEvent);

        console.log('[computed] boardDisplayedItems. Total events count: ', activeItems.length);

        let alreadyAdded: any[] = [];

        let filteredItems = activeItems.filter((item) => {
            //when browser tab is activated, we first do init load for live events, and there is situation when same event is in live array and in today array
            //as result we have duplicates (maybe it is connected with vuetify render, when there is same itemID key)
            if (alreadyAdded.includes(item.customEventId.value + '|' + item.line.value)) {
                console.log(`Skip ${item.customEventId.value} due to duplicate`);
                return false;
            }

            alreadyAdded.push(item.customEventId.value + '|' + item.line.value);

            return (
                item.customEventId.value in favoriteEvent &&
                (filters.line! >= item.line.value || (filters.customEventId !== null && filters.customEventId === item.customEventId.value)) &&
                (!filters.isPositionOnly || item.position.value !== null) &&
                (!filters.isFantasyExclude || item.leagueId.value !== League.Fantasy) &&
                (!filters.search ||
                    searchedLeagueIds.value!.includes(item.leagueId.value) ||
                    searchedTeamIds.value!.includes(item.teamHomeId.value) ||
                    searchedTeamIds.value!.includes(item.teamAwayId.value))
            );
        }) as (FeedLiveItem | FeedPregameItem)[];

        filteredItems.sort(function (x: FeedLiveItem | FeedPregameItem, y: FeedLiveItem | FeedPregameItem) {
            return favoriteEvent[x.customEventId.value] - favoriteEvent[y.customEventId.value];
        });

        console.time(`Render board ${filteredItems.length} items`);
        nextTick().then(() => console.timeEnd(`Render board ${filteredItems.length} items`));
        return filteredItems;
    });

    useBetsChangedHandler(displayedItems as ShallowRef<FeedLiveItem[] | FeedPregameItem[]>);

    return {
        checkEarlyResponse,
        checkLiveResponse,
        checkTodayResponse,
        customColumns,
        displayedItems,
        earlyVersion,
        filters,
        isExtraFiltersDisplayed,
        leagueItems,
        liveVersion,
        loading,
        processEarlyResponse,
        processLiveResponse,
        processTodayResponse,
        reset,
        teamItems,
        todayVersion,
        total,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useFeedBoardStore, import.meta.hot));
}
